import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, ValidationErrors } from '@angular/forms';
import { MAX_SQL_DATE, MIN_SQL_DATE } from 'src/app/exports/constants';
import { StringUtils } from '../utilities/string-utils.service';
import { TimeUnit } from 'src/app/models/web/time-unit.enum';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  public static getValidatorErrorMessage(validatorName: string, validatorValue?: any): string {
    const config = {
      required: 'Required',
      whitespace: 'Required',
      invalidDate: 'Invalid date',
      invalidStartDate: 'Start date must be less than end date',
      maxRangeExceeded: `End date must be within ${validatorValue.maxRange} ${validatorValue.timeUnit} of start date`,
      invalidEmailAddress: 'Invalid email address',
      invalidSocialSecurity: 'Invalid social security number',
      invalidPhoneNumber: 'Invalid phone number',
      invalidZipCode: 'Invalid zip code',
      invalidPassword: 'Invalid password. Password must be at least 6 characters long, and contain a number.',
      minlength: `Minimum length ${validatorValue.requiredLength}`,
      min: `Minimal value ${validatorValue.min}`,
      max: `Maximum value ${validatorValue.max}`,
      invalidPasswordMatch: 'Passwords do not match',
      futureDate: 'Date in future'
    };
    return config[validatorName as keyof typeof config];
  }

  public static dateValidator(control: AbstractControl): ValidationErrors | null {
    // A valid date will be considered one that fits within SQL Server
    const date = new Date(control.value);
    if (date <= MAX_SQL_DATE && date >= MIN_SQL_DATE) {
      return null;
    } else {
      return { invalidDate: true };
    }
  }

  public static optionalDateValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === '') {
      return null;
    } else {
      return ValidationService.dateValidator(control);
    }
  }

  public static startDateValidator(startDateControl: FormControl, endDateControl: FormControl): ValidationErrors | null {
    const startDate = new Date(startDateControl.value);
    const endDate = new Date(endDateControl.value);

    if (StringUtils.isNullUndefinedOrWhitespace(startDateControl.value)
      || StringUtils.isNullUndefinedOrWhitespace(endDateControl.value)
      || startDate <= endDate) {
      return null;
    } else {
      return { invalidStartDate: true };
    }
  }

  public static maxRangeValidator(startDateControl: FormControl, endDateControl: FormControl, maxRange: number, timeUnit: TimeUnit): ValidationErrors | null {
      if (!startDateControl || !endDateControl || !endDateControl.value || !startDateControl.value) {
        return null;
      }

      const startDate = new Date(startDateControl.value);
      const endDate = new Date(endDateControl.value);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return null;
      }

      const diffTime = endDate.getTime() - startDate.getTime();

      switch (timeUnit) {
        case TimeUnit.Days:
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > maxRange) {
            return {maxRangeExceeded: { maxRange, timeUnit }};
          }
          return null;
        case TimeUnit.Months:
          const newMonth = startDate.getMonth() + maxRange;
          let calculatedDateMonth = new Date(startDate.getFullYear(), newMonth, startDate.getDate());
          if (calculatedDateMonth.getDate() !== startDate.getDate()) {
            calculatedDateMonth.setDate(0);
          }
          const maxEndDateMonth = calculatedDateMonth;
          if (endDate > maxEndDateMonth) {
            return { maxRangeExceeded: { maxRange, timeUnit } };
          }
          return null;
        case TimeUnit.Years:
          const newYear = startDate.getFullYear() + maxRange;
          let calculatedDateYear = new Date(newYear, startDate.getMonth(), startDate.getDate());
          if (calculatedDateYear.getDate() !== startDate.getDate()) {
            calculatedDateYear.setDate(0);
          }
          const maxEndDateYear = calculatedDateYear;
          if (endDate > maxEndDateYear) {
            return { maxRangeExceeded: { maxRange, timeUnit } };
          }
          return null;
        default:
          return null;
      }
  }

  public static emailValidator(control: AbstractControl): ValidationErrors | null {
    // RFC 2822 compliant regex
    // eslint-disable-next-line max-len
    if (control.value && (control.value.match(/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/) || control.value.match(/^\s*$/))) {
      return null;
    } else {
      return { invalidEmailAddress: true };
    }
  }

  public static socialSecurityValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value.match(/(^\d{3}-?\d{2}-?\d{4}$)/) || control.value.match(/^\s*$/)) {
      return null;
    } else {
      return { invalidSocialSecurity: true };
    }
  }

  public static socialSecurityWithMaskValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value.match(/(^[0-9*]{3}[-*]?[0-9*]{2}[-*]?\d{4}$)/) || control.value.match(/^\s*$/)) {
      return null;
    } else {
      return { invalidSocialSecurity: true };
    }
  }

  public static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    // eslint-disable-next-line max-len
    if (!control.value || control.value.match(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/) || control.value.match(/^\s*$/)) {
      return null;
    } else {
      return { invalidPhoneNumber: true };
    }
  }

  public static zipCodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value.match(/^\d{5}$/) || control.value.match(/^\d{9}$/) || control.value.match(/^\s*$/)) {
      return null;
    } else {
      return { invalidZipCode: true };
    }
  }

  public static passwordValidator(control: AbstractControl): ValidationErrors | null {
    // {6,100}           - Assert password is between 6 and 100 characters
    // (?=.*[0-9])       - Assert a string has at least one number
    if (control.value.match(/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{6,100}$/)) {
      return null;
    } else {
      return { invalidPassword: true };
    }
  }

  public static validatePasswordsMatch(fieldControl: FormControl, fieldControlToMatch: FormControl): ValidationErrors | null {
    return fieldControl.value === fieldControlToMatch.value
      ? null
      : { invalidPasswordMatch: true };
  }

  public static atleastOneSelectedValidator(control: FormArray): ValidationErrors | null {
    if (control.controls.some(c => c.value === true)) {
      return null;
    } else {
      return { required: true };
    }
  }

  public static controlGreaterThanOtherControl(smallerControl: FormControl, largerControl: FormControl): ValidationErrors | null {
    return +smallerControl.value > +largerControl.value ? {smallerControlLarger: true} : null;
  }

  public static noWhitespaceValidator(control: FormControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  public static futureDateValidator(futureDateControl: FormControl): ValidationErrors | null {
    const dateSelected = new Date(futureDateControl.value);
    const todaysDate = new Date();

    if (dateSelected <= todaysDate) {
      return null;
    } else {
      return { futureDate: true };
    }
  }
}
